import React, { useState } from 'react';

import { Money, formatMoney, toMoney } from '@betterment/core';
import { Box, HStack, Spinner, Text, VStack } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';
import { SambaAutoLink, formatNumberWithDecimals } from '@betterment/shared-components';
import { numberSignFor, textColorFor } from '@betterment/shared-components/src/utils/accountSecurityHelpers';
import { ParentSize } from '@visx/responsive';

import { DateOptionToggleGroup } from '../../../../account/components/overview-card/managed-account-overview/components/balance-toggle-view/DateOptionToggleGroup';
import {
  PerformanceChart,
  formatPerformanceDate,
} from '../../../../account/components/overview-card/managed-account-overview/components/balance-toggle-view/PerformanceChart';
import { PerformanceDisclosure } from '../../../../account/components/overview-card/managed-account-overview/components/balance-toggle-view/PerformanceDisclosure';
import { DateRangeOption } from '../../../../graphql/schema';
import { PerformanceHistoryItemFragment } from '../../../graphql/performanceHistoryItem.generated';
import { DateRangeOptions } from '../../../models/date-range-options';
import { PlaceholderPerformanceChart } from '../../placeholder-performance-chart/PlaceholderPerformanceChart';

const overlayStyles = css.raw({
  bg: 'background.surface',
  opacity: '0.4',
});

const centeredSpinnerStyles = css.raw({
  position: 'absolute',
  top: '50%',
  left: '50%',
  translate: '-50% -50%',
  zIndex: '1',
});

type Props = {
  title?: string;
  balance: Money;
  earned: Money;
  totalReturnPercent?: number | null;
  timeSeries: PerformanceHistoryItemFragment[];
  dateRangeOption: DateRangeOption;
  dateRangeOptions: DateRangeOption[];
  onDateRangeChange: (option: DateRangeOption) => void;
  detailsUrl?: string | null;
  isLoading?: boolean;
  disclosures: {
    recencyDescription: string;
    performanceDisclosure: string;
  };
  balanceTooltip?: React.ReactNode;
};

type PerformanceChartSummaryProps = {
  totalBalance: Money;
  totalEarned: Money | null;
  totalReturnPercent?: number | null;
  dateRangeOption: DateRangeOption;
  highlight: PerformanceHistoryItemFragment | null;
  detailsUrl?: string | null;
  balanceTooltip?: React.ReactNode;
};
const PerformanceChartSummary: React.FC<PerformanceChartSummaryProps> = props => {
  const { totalBalance, totalEarned, dateRangeOption, highlight, detailsUrl, balanceTooltip, totalReturnPercent } =
    props;

  const earned = highlight?.earned || totalEarned;
  const balance = highlight?.balance || totalBalance;

  const dateRangeLabel = dateRangeOption.toLowerCase().replace(/_/g, ' ');
  const titleText = highlight ? formatPerformanceDate(highlight.date) : 'Balance';

  return (
    <VStack>
      <HStack css={{ gap: 'space.50', alignItems: 'center' }}>
        <Text>{titleText}</Text>
        {!highlight && balanceTooltip}
      </HStack>
      <Text variant="text400">{formatMoney(balance)}</Text>
      <HStack css={{ gap: 'space.50', alignItems: 'center' }} data-testid="performance-summary">
        {earned && (
          <Text color={textColorFor(earned.cents, { allowCritical: false })}>
            {numberSignFor(earned.cents)}
            {formatMoney(earned)}
          </Text>
        )}
        {!highlight && (
          <>
            {totalReturnPercent != null && (
              <Text color={textColorFor(totalReturnPercent, { allowCritical: false })}>
                {`(${numberSignFor(totalReturnPercent)}${formatNumberWithDecimals(totalReturnPercent, 2)}%)`}
              </Text>
            )}
            <Text>{dateRangeLabel}</Text>
            {detailsUrl && (
              <SambaAutoLink reloadDocument={true} to={detailsUrl}>
                See details
              </SambaAutoLink>
            )}
          </>
        )}
      </HStack>
      <Text variant="text50" color="text.weak" css={{ visibility: highlight ? 'visible' : 'hidden' }}>
        {highlight ? formatMoney(highlight.invested) : null} net deposits
      </Text>
    </VStack>
  );
};

export const PerformanceSection: React.FC<Props> = ({
  balance,
  earned,
  timeSeries,
  totalReturnPercent,
  dateRangeOption,
  dateRangeOptions,
  onDateRangeChange,
  detailsUrl,
  isLoading = false,
  disclosures,
  balanceTooltip,
}) => {
  const [highlight, setHighlight] = useState<PerformanceHistoryItemFragment | null>(null);

  const shouldShowDateToggle = timeSeries.length > 0;
  const chartData = (() => {
    if (timeSeries.length > 0) return timeSeries;
    if (balance.cents === 0) return [];
    return [
      {
        __typename: 'PerformanceHistoryItem' as const,
        date: new Date(),
        balance,
        invested: balance,
        earned: toMoney(0),
      },
    ];
  })();

  return (
    <VStack css={{ gap: 'space.300' }}>
      <VStack>
        <PerformanceChartSummary
          totalBalance={balance}
          totalEarned={chartData.length > 0 ? earned : null}
          dateRangeOption={dateRangeOption}
          highlight={highlight}
          totalReturnPercent={totalReturnPercent}
          balanceTooltip={balanceTooltip}
          detailsUrl={chartData.length > 0 ? detailsUrl : null}
        />
        <Box>
          {isLoading && <Spinner css={centeredSpinnerStyles} />}
          <Box css={{ ...(isLoading && overlayStyles), width: '100%', height: '100%' }}>
            <ParentSize>
              {({ width }) =>
                chartData.length > 0 ? (
                  <PerformanceChart
                    width={width}
                    height={200}
                    data={chartData}
                    disabled={isLoading}
                    onHighlightChange={setHighlight}
                  />
                ) : (
                  <PlaceholderPerformanceChart width={width} height={200} variant="zero-state" />
                )
              }
            </ParentSize>
          </Box>
        </Box>
      </VStack>

      {shouldShowDateToggle && (
        <HStack css={{ justifyContent: 'center' }}>
          <DateOptionToggleGroup
            value={dateRangeOption}
            onValueChange={(val: DateRangeOption) => val && onDateRangeChange(val)}
          >
            {dateRangeOptions.map(option => (
              <DateOptionToggleGroup.Item key={option} value={option}>
                {DateRangeOptions.labels[option]}
              </DateOptionToggleGroup.Item>
            ))}
          </DateOptionToggleGroup>
        </HStack>
      )}

      <PerformanceDisclosure
        recencyDescription={disclosures.recencyDescription}
        performanceDisclosure={disclosures.performanceDisclosure}
      />
    </VStack>
  );
};
